<template>
	<div class="view-ui-media-zoomableimage">
		<div
			class="image-wrapper"
			v-bind:style="{
				transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
			}"
		>
			<img ref="image" v-bind:src="src" v-on:load="Handle_OnImageLoad" draggable="false" />
		</div>
	</div>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
	import { beforeDestroy, bind, created, mounted } from '@/Utility/Decorators';

	/**
	 * Min/max bounds
	 */
	type TBounds = {
		minX: number;
		maxX: number;
		minY: number;
		maxY: number;
	};

	/**
	 * Image size vector
	 */
	type TImageSize = {
		width: number;
		height: number;
	};

	/**
	 * Position vector
	 */
	type TVector = {
		x: number;
		y: number;
	};

	/**
	 * Zoomable and pannable image component for displaying diagrams or photos
	 * with touch/mouse interaction support.
	 *
	 * Features:
	 *
	 * - Pinch-to-zoom on mobile
	 * - Double-tap/click to zoom in
	 * - Panning with momentum/inertia
	 * - Mouse wheel zoom
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Ui/Media
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewUiMediaZoomableImage extends ViewBase {
		/**
		 * Reference to the image element
		 *
		 * @type HTMLImageElement
		 */
		@Ref('image')
		readonly image!: HTMLImageElement;

		/**
		 * Whether to fit image to container on initial load
		 *
		 * @type boolean
		 */
		@Prop({ default: true })
		public fitToContainer!: boolean;

		/**
		 * Initial zoom level
		 *
		 * @type number
		 */
		@Prop({ default: 1 })
		public initialScale!: number;

		/**
		 * Maximum allowed zoom level
		 *
		 * @type number
		 */
		@Prop({ default: 5 })
		public maxScale!: number;

		/**
		 * Minimum allowed zoom level
		 *
		 * @type number
		 */
		@Prop({ default: 0.5 })
		public minScale!: number;

		/**
		 * Whether to show zoom control buttons
		 *
		 * @type boolean
		 */
		@Prop({ default: false })
		public showControls!: boolean;

		/**
		 * Source URL of the image
		 *
		 * @type string
		 */
		@Prop({ required: true })
		public src!: string;

		/**
		 * Zoom step value for zoom buttons
		 *
		 * @type number
		 */
		@Prop({ default: 0.5 })
		public zoomStep!: number;

		/**
		 * Boundaries for panning to prevent image from moving too far
		 *
		 * @type TBounds
		 */
		protected boundaries: TBounds = {
			maxX: 0,
			maxY: 0,
			minX: 0,
			minY: 0,
		};

		/**
		 * Current position of the image (for panning)
		 *
		 * @type TVector
		 */
		protected position: TVector = {
			x: 0,
			y: 0,
		};

		/**
		 * Current zoom scale
		 *
		 * @type number
		 */
		protected scale: number = 1;

		/**
		 * Image natural dimensions
		 *
		 * @type TImageSize
		 */
		protected imageSize: TImageSize = {
			height: 0,
			width: 0,
		};

		/**
		 * Pointer handler instance
		 *
		 * @type Pointer | null
		 */
		protected pointer!: ChalkySticks.Core.Input.Pointer; // = ChalkySticks.Core.Input.Pointer.instance;

		/**
		 * Component mounted lifecycle hook
		 *
		 * @return void
		 */
		@mounted
		public initializeComponent(): void {
			// Set initial scale
			this.scale = this.initialScale;
		}

		/**
		 * Attach event listeners for interactions
		 *
		 * @return void
		 */
		@mounted
		protected attachEvents(): void {
			this.pointer = new ChalkySticks.Core.Input.Pointer('pointer', true, this.$el as HTMLElement);
			this.pointer.capturePinchZoom(true);

			// Pan/drag handling
			this.pointer.on('drag', this.Handle_OnDrag);

			// Double tap to zoom
			this.pointer.on('doubletap', this.Handle_OnDoubleTap);

			// Pinch/wheel zoom
			this.pointer.on('zoom', this.Handle_OnZoom);

			// Window resize events
			window.addEventListener('resize', this.Handle_OnWindowResize);

			// Animate all the time
			ChalkySticks.Utility.Interval.add(this.Handle_OnUpdateAnimation, 1000 / 24, this.cid + 'animation');
		}

		/**
		 * Detach event listeners
		 *
		 * @return void
		 */
		@beforeDestroy
		protected detachEvents(): void {
			this.pointer?.detachEvents();

			// Remove the window resize listener
			window.removeEventListener('resize', this.Handle_OnWindowResize);

			// Animate all the time
			ChalkySticks.Utility.Interval.remove(this.cid + 'animation');
		}

		/**
		 * Reset zoom and position to initial state
		 *
		 * @return void
		 */
		public resetZoom(): void {
			this.pointer.resetRelative();
			this.scale = this.initialScale;
			this.position = {
				x: 0,
				y: 0,
			};
		}

		/**
		 * Zoom in by the defined step value
		 *
		 * @return void
		 */
		public zoomIn(): void {
			this.scale = Math.min(this.scale + this.zoomStep, this.maxScale);
		}

		/**
		 * Zoom out by the defined step value
		 *
		 * @return void
		 */
		public zoomOut(): void {
			this.scale = Math.max(this.scale - this.zoomStep, this.minScale);
		}

		/**
		 * Constrain current position to boundaries
		 *
		 * @return void
		 */
		protected constrainToBoundaries(): void {
			this.position = {
				x: Math.min(Math.max(this.position.x, this.boundaries.minX), this.boundaries.maxX),
				y: Math.min(Math.max(this.position.y, this.boundaries.minY), this.boundaries.maxY),
			};
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnUpdateAnimation(): Promise<void> {
			// this.constrainToBoundaries();
		}

		/**
		 * Handle image load event
		 *
		 * @return void
		 */
		@bind
		protected Handle_OnImageLoad(): void {
			this.imageSize = {
				height: this.image.naturalHeight,
				width: this.image.naturalWidth,
			};

			this.$emit('load', this.imageSize);
		}

		/**
		 * Handle drag/pan events
		 *
		 * @param object data Drag event data from Pointer
		 * @return void
		 */
		@bind
		protected Handle_OnDrag(e: any): void {
			this.position = {
				x: e.data.rx,
				y: e.data.ry,
			};
		}

		/**
		 * Handle double tap/click to zoom
		 *
		 * @param object data Event data from pointer
		 * @return void
		 */
		@bind
		protected Handle_OnDoubleTap(data?: any): void {
			// Get tap position relative to image for zoom point
			const containerRect = this.$el.getBoundingClientRect();
			const targetX = data ? data.x - containerRect.left : containerRect.width / 2;
			const targetY = data ? data.y - containerRect.top : containerRect.height / 2;

			// Toggle between initial scale and zoomed in
			if (this.scale > this.initialScale * 1.1) {
				this.resetZoom();
			}

			// Zoom in to double the current scale (capped at maxScale)
			else {
				const newScale = Math.min(this.scale * 2, this.maxScale);

				// Calculate position to keep the double-tapped point in place
				const scaleRatio = newScale / this.scale;
				const newX = this.position.x - (targetX - containerRect.width / 2) * (scaleRatio - 1);
				const newY = this.position.y - (targetY - containerRect.height / 2) * (scaleRatio - 1);

				this.scale = newScale;

				// this.position = {
				// 	x: newX,
				// 	y: newY,
				// };
			}
		}

		/**
		 * Handle pinch and wheel zoom events
		 *
		 * @param object data Zoom event data from Pointer
		 * @return void
		 */
		@bind
		protected Handle_OnZoom(e: any): void {
			const containerRect = this.$el.getBoundingClientRect();
			const centerX = containerRect.width / 2;
			const centerY = containerRect.height / 2;
			const data = e.data;

			// Calculate new scale
			let newScale = this.scale;
			const scaleFactor = 0.005;

			if (data.type === 'wheel') {
				// For mouse wheel
				newScale += data.delta * scaleFactor;
			} else if (data.type === 'pinch') {
				// For touch pinch
				if (data.scale) {
					// If we have the absolute scale
					newScale = data.scale * this.initialScale;
				} else {
					// Otherwise use the delta
					newScale += data.delta * scaleFactor;
				}
			}

			// Constrain scale within bounds
			newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

			// Only proceed if scale actually changed
			if (newScale !== this.scale) {
				// Calculate how much the image will grow/shrink
				const scaleRatio = newScale / this.scale;

				// Calculate position to keep the center point in place
				const newX = this.position.x - (centerX - containerRect.width / 2) * (scaleRatio - 1);
				const newY = this.position.y - (centerY - containerRect.height / 2) * (scaleRatio - 1);

				// Update scale and position
				this.scale = newScale;
				this.position = {
					x: newX,
					y: newY,
				};
			}
		}

		/**
		 * Update the boundaries for panning based on current scale
		 *
		 * @return void
		 */
		@bind
		protected Handle_OnWindowResize(): void {
			const containerRect = this.$el.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;

			// Calculate scaled image dimensions
			const scaledWidth = this.imageSize.width * this.scale;
			const scaledHeight = this.imageSize.height * this.scale;

			// Calculate max panning limits
			const maxPanX = Math.max((scaledWidth - containerWidth) / 2, 0);
			const maxPanY = Math.max((scaledHeight - containerHeight) / 2, 0);

			// Set boundaries
			this.boundaries = {
				maxX: maxPanX,
				maxY: maxPanY,
				minX: -maxPanX,
				minY: -maxPanY,
			};
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-ui-media-zoomableimage {
		background-color: var(--chalky-blue-4);
		background-image: url('~@chalkysticks/sass/build/asset/image/branding/icon-blue-3.svg');
		background-position: center center;
		background-repeat: no-repeat;
		background-size: 50% 50%;
		border-radius: var(--border-radius);
		border: 1px solid rgba(255, 255, 255, 0.05);
		height: 100%;
		overflow: hidden;
		position: relative;
		touch-action: none; // Disable browser handling of touch actions
		user-select: none; // Prevent text selection
		width: 100%;

		.image-wrapper {
			align-items: center;
			display: flex;
			height: 100%;
			justify-content: center;
			transform-origin: center center;
			width: 100%;
			will-change: transform; // Optimize for animation performance

			img {
				-webkit-user-drag: none;
				height: auto;
				pointer-events: none; // Prevent browser image dragging
				user-drag: none;
				width: 100%;
			}
		}
	}
</style>
